import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Helper function to make authenticated requests
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const authAxios = {
  get: (url, config = {}) => {
    return axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } });
  },
  post: (url, data, config = {}) => {
    return axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } });
  }
};

function ContentInsertModal({ isOpen, onClose, onInsert, onInsertHtml, textareaRef }) {
  const [files, setFiles] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [activeTab, setActiveTab] = useState('images'); // 'images', 'other', or 'blocks'
  const fileInputRef = useRef(null);

  // Load files and blocks when modal opens (so Blocks tab shows correct count from the start)
  useEffect(() => {
    if (isOpen) {
      fetchFiles();
      fetchBlocks();
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/content/files`);
      if (response.data.success) {
        setFiles(response.data.data.files || []);
      } else {
        setError('Failed to load files');
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlocks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/notes?type=block`);
      if (response.data.success) {
        setBlocks(response.data.data || []);
      } else {
        setError('Failed to load blocks');
      }
    } catch (err) {
      console.error('Error fetching blocks:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load blocks');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);

      const response = await authAxios.post(
        `${API_BASE_URL}/admin/content/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...getAuthHeaders()
          }
        }
      );

      if (response.data.success) {
        // Refresh file list
        await fetchFiles();
        // Select the newly uploaded file
        setSelectedFile(response.data.data);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setError('Failed to upload file');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.error || err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const isImageFile = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
  };

  const generateHTML = (file) => {
    if (file.type === 'image' || isImageFile(file.name)) {
      // Generate <img> tag for images
      const altText = file.name.replace(/\.[^/.]+$/, ''); // Remove extension for alt text
      return `<img src="/api/content/${file.name}" alt="${altText}" class="max-w-full h-auto rounded my-4" />`;
    } else {
      // Generate <a href> tag for other files
      return `<a href="/api/content/${file.name}" download>${file.name}</a>`;
    }
  };

  const handleInsert = () => {
    let contentToInsert = '';

    if (activeTab === 'blocks' && selectedBlock) {
      contentToInsert = `<!-- block:${selectedBlock.slug} -->`;
    } else if (selectedFile) {
      contentToInsert = generateHTML(selectedFile);
    } else {
      return;
    }

    // When used from WYSIWYG (e.g. full-screen note editor), insert raw HTML at cursor via callback
    if (onInsertHtml) {
      onInsertHtml(contentToInsert);
      onClose();
      return;
    }

    if (textareaRef && textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const newText = text.substring(0, start) + contentToInsert + text.substring(end);
      if (onInsert) {
        onInsert(newText, start + contentToInsert.length);
      }
    }
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Filter files based on active tab
  const filteredFiles = files.filter(file => {
    if (activeTab === 'images') {
      return file.type === 'image' || isImageFile(file.name);
    } else {
      return file.type !== 'image' && !isImageFile(file.name);
    }
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Insert Content</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Upload Section - Only show for files tabs */}
        {activeTab !== 'blocks' && (
          <div className="mb-6 p-4 border-2 border-dashed border-gray-300 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload New File
            </label>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {uploading && (
              <p className="mt-2 text-sm text-gray-600">Uploading...</p>
            )}
          </div>
        )}

        {/* File List */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Select File to Insert
            </label>
          </div>
          
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-4">
            <button
              onClick={() => {
                setActiveTab('images');
                setSelectedFile(null);
                setSelectedBlock(null);
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'images'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Images ({files.filter(f => f.type === 'image' || isImageFile(f.name)).length})
            </button>
            <button
              onClick={() => {
                setActiveTab('other');
                setSelectedFile(null);
                setSelectedBlock(null);
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'other'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Other Files ({files.filter(f => f.type !== 'image' && !isImageFile(f.name)).length})
            </button>
            <button
              onClick={() => {
                setActiveTab('blocks');
                setSelectedFile(null);
                setSelectedBlock(null);
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'blocks'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Blocks ({blocks.length})
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : activeTab === 'blocks' ? (
            blocks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No blocks available. Create a block first.
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {blocks.map((block) => (
                  <div
                    key={block.name}
                    onClick={() => setSelectedBlock(block)}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedBlock?.name === block.name
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-medium text-gray-900 truncate">{block.title}</h4>
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded whitespace-nowrap">
                            Block
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          Slug: {block.slug}
                        </div>
                        {block.tags && block.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {block.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                                {tag.toLowerCase()}
                              </span>
                            ))}
                            {block.tags.length > 3 && (
                              <span className="text-xs text-gray-400">+{block.tags.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : filteredFiles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {activeTab === 'images' 
                ? 'No images available. Upload an image to get started.' 
                : 'No other files available. Upload a file to get started.'}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
              {filteredFiles.map((file) => (
                <div
                  key={file.name}
                  onClick={() => setSelectedFile(file)}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    selectedFile?.name === file.name
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {file.type === 'image' ? (
                    <div className="aspect-square mb-2 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                      <img
                        src={`${API_BASE_URL}/content/${file.name}`}
                        alt={file.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="text-gray-400 text-xs">Image</div>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-square mb-2 bg-gray-100 rounded flex items-center justify-center">
                      <div className="text-gray-400 text-2xl">📄</div>
                    </div>
                  )}
                  <div className="text-xs text-gray-700 truncate" title={file.name}>
                    {file.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {formatFileSize(file.size)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview */}
        {activeTab === 'blocks' && selectedBlock ? (
          <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-2">Block Reference:</div>
            <pre className="text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
              {`<!-- block:${selectedBlock.slug} -->`}
            </pre>
            <p className="text-xs text-gray-600 mt-2">
              This will insert a reference to the block. The block content will be rendered when the note is displayed.
            </p>
          </div>
        ) : selectedFile && (
          <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-2">Preview HTML:</div>
            <pre className="text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
              {generateHTML(selectedFile)}
            </pre>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleInsert}
            disabled={!selectedFile && !selectedBlock}
            className={`px-4 py-2 text-white rounded-lg transition-colors ${
              (selectedFile || selectedBlock)
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
}

export default ContentInsertModal;
